import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  ListGroup,
  Spinner,
  Card,
  Alert,
  OverlayTrigger,
  Tooltip,
  InputGroup,
} from "react-bootstrap";
import SurveyWithVariants from "../data/SurveyWithVariants";
import Variant from "../data/Variant";
import { authenticatedFetch } from "../helpFunctions/authFetch";
import { useNavigate } from "react-router-dom";
import { getEnvVariable } from "../helpFunctions/getEnvVariable";
import InfiniteScroll from "react-infinite-scroll-component";

const rankCaptionField = (fields) => {
  const candidates = fields.filter((f) => !/negative/i.test(f) && /prompt/i.test(f.split(".").pop()));
  if (candidates.length === 0) return "";
  candidates.sort((a, b) => {
    const score = (p) => {
      const leaf = p.split(".").pop().toLowerCase();
      if (leaf === "prompt" && !p.includes(".")) return 0;
      if (p === "parameters.prompt") return 1;
      return 2;
    };
    return score(a) - score(b) || a.split(".").length - b.split(".").length || a.length - b.length;
  });
  return candidates[0];
};

const pickJsonField = (fmt, fields) =>
  fmt === "json" ? rankCaptionField(fields || []) : null;

export const validateComparisonSelection = ({
  comparisonScheme = "all_pairs",
  referenceVariantName = null,
  variants = [],
} = {}) => {
  if (comparisonScheme !== "reference_vs_all") return null;

  const activeVariants = variants.filter(
    (variant) => variant.isActive && variant.variantName
  );

  if (!referenceVariantName) {
    return "Select an active reference variant.";
  }

  if (!activeVariants.some((variant) => variant.variantName === referenceVariantName)) {
    return "The reference variant must be active.";
  }

  if (activeVariants.length < 2) {
    return "Reference-vs-all surveys require at least two active variants.";
  }

  return null;
};

// Reconcile the current caption selection against what detection actually
// found, so a stale format/field from a previous folder or variant can't
// linger: drop a selection that's no longer available, auto-pick when there's
// exactly one option, and re-pick the JSON field if the old one is gone.
export const reconcileCaptionSelection = (present, jsonFields, current) => {
  let captionFormat = present.includes(current.captionFormat)
    ? current.captionFormat
    : null;
  if (!captionFormat && present.length === 1) captionFormat = present[0];
  const captionJsonField =
    captionFormat === "json"
      ? jsonFields?.includes(current.captionJsonField)
        ? current.captionJsonField
        : pickJsonField("json", jsonFields)
      : null;
  return { captionFormat, captionJsonField };
};

const SurveyConfig = ({
  surveyData,
  handleSurveyChange,
  serviceAccountEmail,
}) => {
  const createMode = surveyData.surveyId === ""; // create or update view mode
  const apiBaseUrl = getEnvVariable("API_BASE_URL");

  const [surveyCopy, setSurveyCopy] = useState(
    new SurveyWithVariants(surveyData)
  );
  const [validated, setValidated] = useState(false);
  const [newAccess, setNewAccess] = useState({ email: "", role: "viewer" });
  const [isApiKeyValid, setIsApiKeyValid] = useState(
    surveyData.apiKey === "secured"
  ); //true: valid, false: invalid, null: on check
    const [projects, setProjects] = useState(
    surveyData.apiKey === "secured" ? [surveyData.folder] : []
  );
  const [projectSearch, setProjectSearch] = useState("");

  // Debug logging for projects state
  useEffect(() => {
    console.log("[DEBUG] Projects state updated:", projects);
    console.log("[DEBUG] Projects length:", projects.length);
    console.log("[DEBUG] Is API key valid:", isApiKeyValid);
  }, [projects, isApiKeyValid]);
  const [variantSearch, setVariantSearch] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const ITEMS_PER_PAGE = 50;
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [captionFormats, setCaptionFormats] = useState(null); // {txt, json, json_fields} | null
  const [detectingCaptions, setDetectingCaptions] = useState(false);

  const navigate = useNavigate();

  const handleVariantChange = (index) => {
    setSurveyCopy((currentSurvey) => {
      const updatedVariants = currentSurvey.variants.map((variant, variantIndex) =>
        variantIndex === index
          ? { ...variant, isActive: !variant.isActive }
          : variant
      );
      const changedVariant = updatedVariants[index];
      const referenceVariantName =
        changedVariant?.variantName === currentSurvey.referenceVariantName &&
        !changedVariant.isActive
          ? null
          : currentSurvey.referenceVariantName;

      return {
        ...currentSurvey,
        variants: updatedVariants,
        referenceVariantName,
      };
    });
  };

  const isValidGoogleCloudFolder = (url) => {
    // Regular expression to match Google Cloud Storage folder URL format gs:\/\/(?:[\w&&\-]+\/)+
    const googleCloudFolderRegex =
      /^gs:\/\/[a-zA-Z0-9-_]+(?:\/[a-zA-Z0-9-_]+)*\/?$/;
    return googleCloudFolderRegex.test(url);
  };

  const checkFoldersValid = () => {
    const folder = surveyCopy.folder.trim();
    const isValid = isValidGoogleCloudFolder(folder);
    return isValid;
  };

  // Handler for submitting the form
  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const captionsValid =
      surveyCopy.captionFormat !== "json" || Boolean(surveyCopy.captionJsonField);
    const comparisonValid = !validateComparisonSelection(surveyCopy);
    if (!captionsValid || !comparisonValid) {
      setValidated(true);
      return;
    }

    setValidated(true);

    const form = e.currentTarget;
    if (
      form.checkValidity() &&
      ((surveyCopy.source === "GCS" && checkFoldersValid()) ||
        (surveyCopy.source === "W&B" && isApiKeyValid))
    ) {
      const activeVariants = surveyCopy.variants.filter(
        (variant) => variant.isActive
      );

      const mergedVariants = createMode
        ? activeVariants
        : (() => {
            // First, update existing variants with their new isActive status
            const updatedExisting = surveyData.variants.map((existingVariant) => {
            const matchingVariant = surveyCopy.variants.find(
              (variant) => variant.variantName === existingVariant.variantName
            );
            return {
              ...existingVariant,
              isActive: matchingVariant
                ? matchingVariant.isActive
                : existingVariant.isActive,
            };
          });

            // Then, add any new variants that were selected
            const newVariants = surveyCopy.variants.filter(
              (variant) => !surveyData.variants.some(
                (existing) => existing.variantName === variant.variantName
              )
            );

            // Combine existing (updated) and new variants
            return [...updatedExisting, ...newVariants];
          })();

      handleSurveyChange({
        ...surveyCopy,
        variants: mergedVariants,
        user_role: "manager",
      });
      console.log("Form submitted:", {
        ...surveyCopy,
        variants: mergedVariants,
      });
    }
  };

  const deleteSurvey = () => {
    handleSurveyChange(surveyCopy.surveyId);
    console.log("Survey delete");
  };

  const convertVariantsByNames = (variants) => {
    return variants.map((variant) => {
      return new Variant({
        variantId: 0,
        variantName: variant.name,
        isActive: surveyCopy.variantActiveByDefault,
        wandbId: variant.id,
      });
    });
  };

  const addAccess = () => {
    if (newAccess.email && newAccess.role) {
      const updatedSurveyCopy = new SurveyWithVariants(surveyCopy);
      updatedSurveyCopy.addAccess({
        email: newAccess.email,
        role: newAccess.role,
      });
      setSurveyCopy(updatedSurveyCopy);
      setNewAccess({ email: "", role: "viewer" });
    }
  };

  const removeAccess = (accessId) => {
    const updatedAccess = surveyCopy.access.filter(
      (item) => item.accessId !== accessId
    );
    setSurveyCopy({ ...surveyCopy, access: updatedAccess });
  };

  // Removed automatic validation based on length only
  // API key must be validated with backend before being marked as valid

  const validateApiKey = async (apiKey, surveyId) => {
    console.log("validateApiKey called with:", { apiKey: apiKey.substring(0, 10) + "...", surveyId });

    if (apiKey.length !== 40 && apiKey !== "secured") {
      console.log("API key invalid length:", apiKey.length);
      setIsApiKeyValid(false);
      return;
    }

    try {
      console.log("Starting API key validation...");
      setIsApiKeyValid(null);

      const url = `${apiBaseUrl}validateApiKey?survey_id=${Number(surveyId) || 0}`;
      console.log("Validation URL:", url);

      const response = await authenticatedFetch(
        navigate,
        url,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(apiKey),
        }
      );

      console.log("Validation response status:", response.status);

      if (response.ok) {
        const responseText = await response.text();
        console.log("Raw response text:", responseText);

        let responseJson;
        try {
          responseJson = JSON.parse(responseText);
        } catch (e) {
          console.error("Failed to parse JSON:", e);
          console.log("Response was:", responseText);
          setIsApiKeyValid(false);
          return;
        }

        console.log("Parsed response:", responseJson);
        console.log("Projects array:", responseJson.projects);
        console.log("Projects is array?", Array.isArray(responseJson.projects));
        console.log("Projects length:", responseJson.projects ? responseJson.projects.length : "undefined");

        setIsApiKeyValid(true);
        setProjects(responseJson.projects || []);
      } else {
        console.log("Validation failed, status:", response.status);
        const errorText = await response.text();
        console.log("Error response:", errorText);
        setIsApiKeyValid(false);
      }
    } catch (error) {
      console.error("Error validating API key:", error);
      setIsApiKeyValid(false);
      alert("An error occurred while validating the API key");
    }
  };

  const handleProjectChange = (e) => {
    // Clear variants when project changes
    setSurveyCopy({
      ...surveyCopy,
      folder: e.target.value,
      variants: [], // Clear the variants list
      referenceVariantName: null,
    });
    // Reset variant-related state
    setVariantSearch("");
    setPage(1);
    setHasMore(false);
    // getVariantsList(e.target.value, surveyCopy.apiKey, surveyCopy.source);
  };

  const loadVariants = async (searchTerm = "", pageNum = 1) => {
    if (
      !(surveyCopy.folder && (surveyCopy.source === "GCS" || surveyCopy.apiKey))
    ) {
      return;
    }

    if (loading) return;

    setLoading(true);
    try {
      const encodedSourceType = encodeURIComponent(surveyCopy.source);
      const response = await authenticatedFetch(
        navigate,
        `${apiBaseUrl}variants?folder=${surveyCopy.folder}&survey_id=${
          Number(surveyCopy.surveyId) || 0
        }&source=${encodedSourceType}&search=${searchTerm}&page=${pageNum}&per_page=${ITEMS_PER_PAGE}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(surveyCopy.apiKey),
        }
      );

      const responseJson = await response.json();
      if (response.ok) {
        const newVariants = responseJson.variants;

        // The following logic ensures that we do not remove old variants because they have the 'isActive' property that the user chose.
        // We also cannot just add the new variants and ignore duplicate variants.
        // Therefore, we filter and add only the variants that do not already exist.
        const variantsToAdd = newVariants.filter(
          (newVariant) =>
            !surveyCopy.variants.some(
              (existingVariant) =>
                existingVariant.variantName === newVariant.name
            )
        );
        const filteredOldVariants = surveyCopy.variants.filter(
          (oldVariant) =>
            !newVariants.some(
              (newVariant) => oldVariant.variantName === newVariant.name
            )
        );
        const variants = convertVariantsByNames(variantsToAdd);
        const existingVariants = surveyCopy.variants.filter((variant) =>
          newVariants.some(
            (newVariant) => variant.variantName === newVariant.name
          )
        );
        const combVariants = surveyCopy.variants[0]?.variantName
          ? [...variants, ...existingVariants, ...filteredOldVariants]
          : variants;
        setSurveyCopy({ ...surveyCopy, variants: combVariants });

        setHasMore(responseJson.hasMore);
      } else {
        console.log("🚀 ~ loadVariants ~ responseJson:", responseJson);
        alert(
          `An error occurred while loading variants: ${
            responseJson?.detail || "Unknown error"
          }`
        );
      }
    } catch (error) {
      console.error("Error loading variants:", error);
      alert("An error occurred while loading variants");
    } finally {
      setLoading(false);
    }
  };

  // Sample captions from the variant the survey will actually use (first
  // active, falling back to the first variant), not just the first subfolder.
  const detectVariantName = (
    surveyCopy.variants?.find((v) => v.isActive) || surveyCopy.variants?.[0]
  )?.variantName;

  // Drop any caption selection so a stale format/field can't be submitted when
  // captions are unavailable (wrong source, missing folder/variant, or a failed
  // detection request).
  const clearCaptionSelection = () =>
    setSurveyCopy((s) =>
      s.captionFormat == null && s.captionJsonField == null
        ? s
        : { ...s, captionFormat: null, captionJsonField: null }
    );

  const variantFields = surveyCopy.variantFields || [];
  const isFieldSelected = (path) => variantFields.some((f) => f.field === path);
  const detectedDisplayFields = captionFormats?.display_fields || [];
  // Render the union of detected fields and already-selected fields, so a
  // selection that detection no longer surfaces stays visible (and uncheckable)
  // rather than being silently persisted while hidden. Set-dedup guards against
  // any repeated path (duplicate React keys / checkbox ids).
  const visibleFieldPaths = [
    ...new Set([...detectedDisplayFields, ...variantFields.map((f) => f.field)]),
  ];

  const toggleVariantField = (path, checked) =>
    setSurveyCopy((s) => {
      const current = s.variantFields || [];
      const next = checked
        ? [...current, { field: path, label: path }]
        : current.filter((f) => f.field !== path);
      return { ...s, variantFields: next };
    });

  const setVariantFieldLabel = (path, label) =>
    setSurveyCopy((s) => ({
      ...s,
      variantFields: (s.variantFields || []).map((f) =>
        f.field === path ? { ...f, label } : f
      ),
    }));

  const detectCaptionFormats = async () => {
    if (surveyCopy.source !== "GCS" || !surveyCopy.folder || !detectVariantName) {
      setCaptionFormats(null);
      clearCaptionSelection();
      return;
    }
    setDetectingCaptions(true);
    try {
      const res = await authenticatedFetch(
        navigate,
        `${apiBaseUrl}captionFormats?folder=${encodeURIComponent(surveyCopy.folder)}&survey_id=${
          Number(surveyCopy.surveyId) || 0
        }&source=${encodeURIComponent(surveyCopy.source)}&variant=${encodeURIComponent(
          detectVariantName
        )}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(surveyCopy.apiKey || ""),
        }
      );
      if (!res.ok) {
        console.error("caption format detection failed:", res.status);
        setCaptionFormats(null);
        clearCaptionSelection();
        return;
      }
      const data = await res.json();
      setCaptionFormats(data);
      const present = [data.txt && "txt", data.json && "json"].filter(Boolean);
      setSurveyCopy((s) => {
        const next = reconcileCaptionSelection(present, data.json_fields, s);
        return next.captionFormat === s.captionFormat &&
          next.captionJsonField === s.captionJsonField
          ? s
          : { ...s, ...next };
      });
    } catch (e) {
      console.error("caption format detection failed:", e);
      setCaptionFormats(null);
      clearCaptionSelection();
    } finally {
      setDetectingCaptions(false);
    }
  };

  const handleSearch = (searchTerm) => {
    setVariantSearch(searchTerm);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const newTimeout = setTimeout(() => {
      setPage(1);
      loadVariants(searchTerm, 1);
    }, 300);

    setSearchTimeout(newTimeout);
  };

  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  useEffect(() => {
    const timeout = setTimeout(() => detectCaptionFormats(), 500);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyCopy.folder, detectVariantName, surveyCopy.source]);

  const handleGetVariants = () => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setVariantSearch("");
    setPage(1);
    loadVariants("", 1);
  };

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadVariants(variantSearch, nextPage);
  };

  return (
    <Container fluid className="px-3 py-2">
      <Card className="shadow-sm">
        <Card.Body className="p-3">
          <h4 className="mb-3 text-muted">
            {createMode ? "Create New Survey" : "Edit Survey"}
          </h4>
          <Form
            noValidate
            validated={validated}
            onSubmit={handleSubmit}
            className="compact-form"
          >
            {/* Survey Name */}
            <Form.Group controlId="surveyName" className="mb-2">
              <Form.Label className="mb-1">
                <small>Survey Name:</small>
              </Form.Label>
              <Form.Control
                type="text"
                size="sm"
                value={surveyCopy.surveyName}
                onChange={(e) =>
                  setSurveyCopy({ ...surveyCopy, surveyName: e.target.value })
                }
                required
                placeholder="Enter survey name"
              />
              <Form.Control.Feedback type="invalid">
                Survey name is required.
              </Form.Control.Feedback>
            </Form.Group>

            {/* Survey Title */}
            <Form.Group controlId="surveyTitle" className="mb-2">
              <Form.Label className="mb-1">
                <small>Survey Title:</small>
              </Form.Label>
              <Form.Control
                type="text"
                size="sm"
                value={surveyCopy.surveyTitle}
                onChange={(e) =>
                  setSurveyCopy({ ...surveyCopy, surveyTitle: e.target.value })
                }
                placeholder="Which is better?"
              />
            </Form.Group>

            {/* Source Selection */}
            <Form.Group controlId="sourceSelect" className="mb-2">
              <Form.Label className="mb-1">
                <small>Source:</small>
              </Form.Label>
              <Form.Select
                size="sm"
                value={surveyCopy.source}
                onChange={(e) => {
                  setSurveyCopy({
                    ...surveyCopy,
                    folder: "",
                    variantActiveByDefault: false,
                    variants: [],
                    source: e.target.value,
                    referenceVariantName: null,
                  });
                }}
                required
              >
                <option value="">Select a source</option>
                <option value="W&B">Weights & Biases</option>
                <option value="GCS">Google Cloud Storage</option>
              </Form.Select>
            </Form.Group>

            {/* API Key (for W&B) */}
            {surveyCopy.source !== "GCS" && (
              <Form.Group controlId="apiKey" className="mb-2">
                <Form.Label className="mb-1">
                  <small>API Key:</small>
                </Form.Label>
                <Form.Control
                  type="password"
                  size="sm"
                  value={surveyCopy.apiKey}
                  onChange={(e) => {
                    setSurveyCopy({ ...surveyCopy, apiKey: e.target.value });
                    // Validate immediately when API key is 40 characters
                    if (e.target.value.length === 40) {
                      validateApiKey(e.target.value, surveyCopy.surveyId);
                    } else if (e.target.value.length !== 40 && e.target.value !== "secured") {
                      setIsApiKeyValid(false);
                    }
                  }}
                  onBlur={(e) =>
                    validateApiKey(e.target.value, surveyCopy.surveyId)
                  }
                  required
                  placeholder={
                    createMode || surveyData.source !== "W&B"
                      ? "Enter API Key"
                      : "Update API Key"
                  }
                />
                <Form.Text className="text-muted">
                  {surveyCopy.apiKey !== "secured"
                    ? "Required for access to W&B API."
                    : "Enter API key only if you want update it."}
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  API Key is required.
                </Form.Control.Feedback>
              </Form.Group>
            )}

            {/* Project Selection (for W&B) */}
            {surveyCopy.source === "W&B" && (
              <Form.Group controlId="projectSelect" className="mb-2">
                <Form.Label className="mb-1">
                  <small>Project:</small>
                </Form.Label>

                {/* Project Search Input */}
                {isApiKeyValid && projects.length > 10 && (
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder="Search projects..."
                    value={projectSearch}
                    onChange={(e) => setProjectSearch(e.target.value)}
                    className="mb-2"
                  />
                )}

                <Form.Select
                  size="sm"
                  value={surveyCopy.folder}
                  onChange={(e) => handleProjectChange(e)}
                  disabled={!isApiKeyValid}
                >
                  {isApiKeyValid === null && (
                    <option>Validating API key...</option>
                  )}
                  {isApiKeyValid === false && (
                    <option>Enter valid API key</option>
                  )}
                  {isApiKeyValid &&
                    surveyCopy.folder.length &&
                    projects.length === 0 && <option>No projects found</option>}
                  {isApiKeyValid &&
                    !surveyCopy.folder &&
                    projects.length > 0 && (
                      <option value="">Select project</option>
                    )}
                  {isApiKeyValid && projects.length === 0 && (
                    <option value="">No projects found (check console)</option>
                  )}
                  {isApiKeyValid &&
                    projects.length > 0 &&
                    projects
                      .filter((project) =>
                        project.toLowerCase().includes(projectSearch.toLowerCase())
                      )
                      .map((project) => (
                        <option key={project} value={project}>
                          {project}
                        </option>
                      ))}
                </Form.Select>

                {/* Show count of filtered projects */}
                {isApiKeyValid && projects.length > 0 && projectSearch && (
                  <Form.Text className="text-muted">
                    Showing {projects.filter(p => p.toLowerCase().includes(projectSearch.toLowerCase())).length} of {projects.length} projects
                  </Form.Text>
                )}

                <Form.Control.Feedback type="invalid">
                  Select project.
                </Form.Control.Feedback>
              </Form.Group>
            )}

            {/* Folder Path (for GCS) */}
            {surveyCopy.source === "GCS" && (
              <Form.Group controlId="foldersSelect" className="mb-2">
                <Form.Label className="mb-1">
                  <small>Folder Path:</small>
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip id="folder-path-tooltip">
                        Enter valid gsutil URI. Folder must contain variant
                        sub-folders with matching file names across variants.
                      </Tooltip>
                    }
                  >
                    <span className="ms-1 text-primary">ⓘ</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  required
                  pattern={"gs://(?:[w&&-]+/)+"}
                  isInvalid={!checkFoldersValid() && validated}
                  value={surveyCopy.folder}
                  onChange={(e) =>
                    setSurveyCopy({ ...surveyCopy, folder: e.target.value })
                  }
                  placeholder="gs://bucket_name/path_to_folder"
                />
                <Form.Text className="text-muted">
                  Make sure to grant the 'Cloud Storage Admin' role to the
                  service account: "{serviceAccountEmail}
                  ".
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  Enter valid gsutil URI
                </Form.Control.Feedback>
              </Form.Group>
            )}

            {/* Get Variants List Button */}
            <Button
              className="mb-2"
              variant="outline-primary"
              size="sm"
              type="button"
              onClick={handleGetVariants}
              disabled={
                !(
                  surveyCopy.folder &&
                  (surveyCopy.source === "GCS" || surveyCopy.apiKey)
                ) || loading
              }
            >
              {loading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                  <span className="ms-2">Loading...</span>
                </>
              ) : (
                "Get Variants List"
              )}
            </Button>

            {/* Variant Active by Default Switch */}
            <Form.Group controlId="activeDefault" className="mb-2">
              <Form.Check
                type="switch"
                id="variantActiveByDefault"
                label={
                  <small>
                    {surveyCopy.source === "GCS"
                      ? "Variant active by default"
                      : "Run is added by default"}
                  </small>
                }
                checked={surveyCopy.variantActiveByDefault}
                onChange={(e) =>
                  setSurveyCopy({
                    ...surveyCopy,
                    variantActiveByDefault: e.target.checked,
                  })
                }
              />
            </Form.Group>

            {/* Comparison scheme */}
            <Form.Group controlId="comparisonSchemeControl" className="mb-2">
              <Form.Label className="mb-1">
                <small>Comparison scheme:</small>
              </Form.Label>
              <Form.Select
                size="sm"
                value={surveyCopy.comparisonScheme || "all_pairs"}
                onChange={(e) =>
                  setSurveyCopy((currentSurvey) => ({
                    ...currentSurvey,
                    comparisonScheme: e.target.value,
                    referenceVariantName:
                      e.target.value === "reference_vs_all"
                        ? currentSurvey.referenceVariantName
                        : null,
                  }))
                }
              >
                <option value="all_pairs">All pairs</option>
                <option value="reference_vs_all">Reference vs every candidate</option>
              </Form.Select>
              <Form.Text className="text-muted">
                <small>
                  Reference mode compares one active variant against each other
                  variant.
                </small>
              </Form.Text>
            </Form.Group>

            {surveyCopy.comparisonScheme === "reference_vs_all" && (
              <Form.Group controlId="referenceVariantControl" className="mb-2">
                <Form.Label className="mb-1">
                  <small>Reference variant:</small>
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={surveyCopy.referenceVariantName || ""}
                  isInvalid={
                    validated && Boolean(validateComparisonSelection(surveyCopy))
                  }
                  onChange={(e) =>
                    setSurveyCopy({
                      ...surveyCopy,
                      referenceVariantName: e.target.value || null,
                    })
                  }
                >
                  <option value="">Select an active reference</option>
                  {surveyCopy.variants
                    .filter((variant) => variant.isActive && variant.variantName)
                    .map((variant) => (
                      <option
                        key={variant.variantName}
                        value={variant.variantName}
                      >
                        {variant.variantName}
                      </option>
                    ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {validateComparisonSelection(surveyCopy)}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  <small>
                    This variant will be compared adaptively against every
                    other active variant.
                  </small>
                </Form.Text>
              </Form.Group>
            )}

            {/* Caption format */}
            <Form.Group controlId="captionFormatControl" className="mb-2">
              <Form.Label className="mb-1"><small>Captions</small></Form.Label>
              <Form.Select
                size="sm"
                value={surveyCopy.captionFormat || ""}
                disabled={surveyCopy.source !== "GCS" || !captionFormats || detectingCaptions}
                onChange={(e) => {
                  const fmt = e.target.value || null;
                  const field = pickJsonField(fmt, captionFormats?.json_fields);
                  setSurveyCopy({ ...surveyCopy, captionFormat: fmt, captionJsonField: field });
                }}
              >
                <option value="">No caption</option>
                {captionFormats?.txt && <option value="txt">.txt files</option>}
                {captionFormats?.json && <option value="json">.json files</option>}
              </Form.Select>
              {surveyCopy.source === "GCS" && (!captionFormats || detectingCaptions) && (
                <Form.Text muted>
                  {detectingCaptions ? "Detecting caption formats…" : "Select a folder and variants to detect caption formats."}
                </Form.Text>
              )}
            </Form.Group>

            {/* W&B captions: binary toggle (captions come from the run summary,
                so there is no sidecar format to detect/pick) */}
            {surveyCopy.source !== "GCS" && (
              <Form.Group controlId="useCaptionsControl" className="mb-2">
                <Form.Check
                  type="switch"
                  id="useCaptions"
                  label={<small>Use captions if exist</small>}
                  checked={Boolean(surveyCopy.useCaptions)}
                  onChange={(e) =>
                    setSurveyCopy({ ...surveyCopy, useCaptions: e.target.checked })
                  }
                />
              </Form.Group>
            )}

            {/* JSON caption field */}
            {surveyCopy.captionFormat === "json" && (
              <Form.Group controlId="captionJsonFieldControl" className="mb-2">
                <Form.Label className="mb-1"><small>Caption field (JSON)</small></Form.Label>
                <Form.Select
                  size="sm"
                  value={surveyCopy.captionJsonField || ""}
                  isInvalid={validated && !surveyCopy.captionJsonField}
                  onChange={(e) => setSurveyCopy({ ...surveyCopy, captionJsonField: e.target.value || null })}
                >
                  <option value="">Select a field…</option>
                  {(captionFormats?.json_fields || []).map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid"><small>Pick which JSON field holds the caption.</small></Form.Control.Feedback>
              </Form.Group>
            )}

            {/* Additional per-variant fields (read from each variant's JSON
                sidecar, shown under its video; value shape picks the renderer).
                Render the union of detected + selected so a stale selection is
                visible/uncheckable rather than invisibly persisted. */}
            {surveyCopy.source === "GCS" && visibleFieldPaths.length > 0 && (
              <Form.Group controlId="variantFieldsControl" className="mb-2">
                <Form.Label className="mb-1"><small>Additional per-variant fields</small></Form.Label>
                {visibleFieldPaths.map((path) => {
                  const notDetected = !detectedDisplayFields.includes(path);
                  return (
                    <div key={path} className="mb-1">
                      <Form.Check
                        type="checkbox"
                        id={`variant-field-${path}`}
                        label={<small>{path}{notDetected ? " (not in current sample)" : ""}</small>}
                        checked={isFieldSelected(path)}
                        onChange={(e) => toggleVariantField(path, e.target.checked)}
                      />
                      {isFieldSelected(path) && (
                        <Form.Control
                          size="sm"
                          className="mt-1"
                          placeholder="Display label (optional)"
                          value={variantFields.find((f) => f.field === path)?.label || ""}
                          onChange={(e) => setVariantFieldLabel(path, e.target.value)}
                        />
                      )}
                    </div>
                  );
                })}
                <Form.Text muted>
                  <small>Shown under each video. The sidecar value's shape selects the display style.</small>
                </Form.Text>
              </Form.Group>
            )}

            {/* Manage Variants */}
            <Form.Group controlId="variantsManagement" className="mb-2">
              <Form.Label className="mb-1">
                <small>Manage Variants:</small>
              </Form.Label>
              {!surveyCopy.variants.length ? (
                <Alert variant="info" className="py-1 px-2">
                  <small>There are no variants.</small>
                </Alert>
              ) : (
                surveyCopy.variants[0].variantName && (
                  <>
                    {/* Search Input */}
                    {surveyCopy.variants.length > 0 && (
                      <InputGroup size="sm" className="mb-2">
                        <Form.Control
                          type="text"
                          placeholder="Search variants..."
                          value={variantSearch}
                          onChange={(e) => handleSearch(e.target.value)}
                        />
                        {variantSearch && (
                          <Button
                            variant="outline-secondary"
                            onClick={() => {
                              if (searchTimeout) {
                                clearTimeout(searchTimeout);
                              }
                              handleSearch("");
                            }}
                          >
                            Clear
                          </Button>
                        )}
                      </InputGroup>
                    )}
                    <div
                      id="variantsContainer"
                      className="border rounded p-2"
                      style={{ maxHeight: "200px", overflowY: "auto" }}
                    >
                      <InfiniteScroll
                        dataLength={surveyCopy.variants.length}
                        next={loadMore}
                        hasMore={hasMore}
                        loader={
                          <small className="text-muted">Loading...</small>
                        }
                        scrollableTarget="variantsContainer"
                      >
                        {surveyCopy.variants.map(
                          (variant, index) =>
                            variant.variantName && (
                              <Form.Check
                                key={`${variant.variantName}-${index}`}
                                type="switch"
                                id={`variant-${index}`}
                                label={<small>{variant.variantName}</small>}
                                checked={variant.isActive}
                                onChange={() => handleVariantChange(index)}
                                className="mb-1"
                              />
                            )
                        )}
                      </InfiniteScroll>
                      {!loading && surveyCopy.variants.length === 0 && (
                        <small className="text-muted">
                          No matching variants found.
                        </small>
                      )}
                    </div>
                  </>
                )
              )}
            </Form.Group>

            {/* Manage Access */}
            <Form.Group controlId="accessManagement" className="mb-2">
              <Form.Label className="mb-1">
                <small>Manage Access:</small>
              </Form.Label>
              <ListGroup className="mb-2 border rounded">
                {!surveyCopy.access.length ? (
                  <ListGroup.Item className="py-1 px-2">
                    <small>No access entries</small>
                  </ListGroup.Item>
                ) : (
                  surveyCopy.access.map((item, index) => (
                    <ListGroup.Item
                      key={index}
                      className="py-1 px-2 d-flex justify-content-between align-items-center"
                    >
                      <small>
                        {item.email || item.groupEmail || item.userId} -{" "}
                        {item.role}
                      </small>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="py-0 px-1"
                        onClick={() => removeAccess(item.accessId)}
                      >
                        <small>Remove</small>
                      </Button>
                    </ListGroup.Item>
                  ))
                )}
              </ListGroup>
              <Row className="g-1">
                <Col sm={6}>
                  <Form.Control
                    size="sm"
                    type="email"
                    placeholder="Enter email"
                    value={newAccess.email}
                    onChange={(e) =>
                      setNewAccess({ ...newAccess, email: e.target.value })
                    }
                  />
                </Col>
                <Col sm={4}>
                  <Form.Select
                    size="sm"
                    value={newAccess.role}
                    onChange={(e) =>
                      setNewAccess({ ...newAccess, role: e.target.value })
                    }
                  >
                    <option value="viewer">Viewer</option>
                    <option value="voter">Voter</option>
                    <option value="manager">Manager</option>
                  </Form.Select>
                </Col>
                <Col sm={2}>
                  <Button
                    variant="outline-success"
                    size="sm"
                    className="w-100"
                    onClick={addAccess}
                  >
                    Add
                  </Button>
                </Col>
              </Row>
            </Form.Group>

            {/* Submit and Delete Buttons */}
            <div className="d-flex justify-content-between mt-3">
              <Button variant="primary" size="sm" type="submit">
                {createMode ? "Create Survey" : "Update Survey"}
              </Button>
              {!createMode && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  type="button"
                  onClick={deleteSurvey}
                >
                  Delete Survey
                </Button>
              )}
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default SurveyConfig;
